import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, AlertTriangle, Flame } from 'lucide-react';
const CART_URL = 'https://guiaspracticas.myshopify.com//cart/add?id=51259935031586&quantity=1';
const TIMER_DURATION = 10 * 60; // 10 minutes in seconds
export function FinalCtaSection() {
  const [timeLeft, setTimeLeft] = useState(TIMER_DURATION);
  const [isUrgent, setIsUrgent] = useState(false);
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          return TIMER_DURATION; // Reset when it hits 0
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    setIsUrgent(timeLeft <= 120); // Last 2 minutes = urgent
  }, [timeLeft]);
  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);
  const handleCtaClick = () => {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: 'The F-Formula',
        content_type: 'product',
        value: 18.9,
        currency: 'USD'
      });
    }
  };
  return <section id="final-cta" className="py-32 bg-primary relative overflow-hidden" data-id="element-43">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-gold/10 via-primary to-primary opacity-50 pointer-events-none" data-id="element-44" />

      <div className="max-w-4xl mx-auto px-6 text-center relative z-10" data-id="element-45">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-46">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight" data-id="element-47">
            No estás roto. <br data-id="element-48" />
            <span className="text-gray-500" data-id="element-49">
              Solo estás siguiendo el orden equivocado.
            </span>
          </h2>

          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto" data-id="element-50">
            The F-Formula te da la estructura que necesitás para generar
            atracción real.
          </p>

          {/* Urgency Banner */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-8 border transition-colors duration-500 ${isUrgent ? 'bg-red-950/40 border-red-800/50 text-red-300' : 'bg-gold/10 border-gold/20 text-gold'}`} data-id="element-51">
            <Flame size={16} className={isUrgent ? 'text-red-400 animate-pulse' : 'text-gold'} data-id="element-52" />
            <span className="text-sm font-semibold uppercase tracking-wider" data-id="element-53">
              Solo por hoy — Oferta limitada
            </span>
          </motion.div>

          {/* Countdown Timer */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="mb-10" data-id="element-54">
            <p className="text-sm text-gray-400 uppercase tracking-widest mb-4 flex items-center justify-center gap-2" data-id="element-55">
              <Clock size={14} className="text-gold" data-id="element-56" />
              Este precio se va en
            </p>
            <div className="flex items-center justify-center gap-3" data-id="element-57">
              {/* Minutes */}
              <div className="flex flex-col items-center" data-id="element-58">
                <div className={`text-5xl md:text-7xl font-black tabular-nums tracking-tight transition-colors duration-500 ${isUrgent ? 'text-red-400' : 'text-white'}`} data-id="element-59">
                  {Math.floor(timeLeft / 60).toString().padStart(2, '0')}
                </div>
                <span className="text-xs text-gray-500 uppercase tracking-widest mt-1" data-id="element-60">
                  min
                </span>
              </div>

              {/* Separator */}
              <div className={`text-4xl md:text-6xl font-black mb-4 animate-pulse ${isUrgent ? 'text-red-400' : 'text-gold'}`} data-id="element-61">
                :
              </div>

              {/* Seconds */}
              <div className="flex flex-col items-center" data-id="element-62">
                <div className={`text-5xl md:text-7xl font-black tabular-nums tracking-tight transition-colors duration-500 ${isUrgent ? 'text-red-400' : 'text-white'}`} data-id="element-63">
                  {(timeLeft % 60).toString().padStart(2, '0')}
                </div>
                <span className="text-xs text-gray-500 uppercase tracking-widest mt-1" data-id="element-64">
                  seg
                </span>
              </div>
            </div>
          </motion.div>

          {/* Pricing */}
          <div className="flex items-center justify-center gap-4 mb-4" data-id="element-65">
            <span className="text-gray-500 line-through text-2xl font-medium" data-id="element-66">
              $45
            </span>
            <span className="text-5xl md:text-6xl font-black text-gold" data-id="element-67">
              $18<span className="text-3xl md:text-4xl" data-id="element-68">,90</span>
            </span>
          </div>

          {/* Savings badge */}
          <div className="mb-10" data-id="element-69">
            <span className="inline-block bg-gold/15 text-gold text-sm font-bold px-4 py-1.5 rounded-full border border-gold/20" data-id="element-70">
              Ahorrás $26,10 — 58% OFF
            </span>
          </div>

          <div className="flex flex-col items-center space-y-4" data-id="element-71">
            <a href={CART_URL} onClick={handleCtaClick} target="_blank" rel="noopener noreferrer" className={`inline-block font-bold text-xl py-6 px-16 rounded-sm transition-all duration-300 transform hover:scale-[1.02] w-full md:w-auto text-center ${isUrgent ? 'bg-gold hover:bg-gold-light text-primary shadow-[0_0_50px_rgba(201,168,76,0.5)] animate-pulse' : 'bg-gold hover:bg-gold-light text-primary shadow-[0_0_40px_rgba(201,168,76,0.4)]'}`} data-id="element-72">
              Acceder ahora a The F-Formula
            </a>

            <div className="flex flex-col items-center gap-2 pt-4" data-id="element-73">
              <div className="flex items-center gap-2 text-sm text-gray-400" data-id="element-74">
                <AlertTriangle size={14} className="text-gold/70" data-id="element-75" />
                <span data-id="element-76">Quedan pocas unidades a este precio</span>
              </div>
              <p className="text-sm text-gold/80 font-medium tracking-wide uppercase" data-id="element-77">
                Acceso limitado — Precio especial por lanzamiento
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}